import { unwrapResult } from "@reduxjs/toolkit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LaunchIcon from "@mui/icons-material/Launch";
import { Alert, Box, Button, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState } from "react";
import { APIError, AppId } from "../../../../app/appTypes";
import { LpField } from "../../../../app/components/LpField";
import { useAppDispatch } from "../../../../app/hooks";
import { DocumentStatusChip } from "../../../documents/controls/DocumentStatusChip";
import { selectHasFeatureFlag, dynamicUrsFeatureFlag } from "../../../settings/settingsSlice";
import { useAppSelector } from "../../../../app/hooks";
import { CreateDynamicUrsDialog } from "../../../urs/CreateDynamicUrsDialog";
import { Urs } from "../../../urs/UrsSlice";
import { LinkValidationProjectDocumentDialog, LinkValidationProjectDocumentDialogProps } from "../dialogs/LinkValidationProjectDocumentDialog";
import { linkValidationProjectDocument, loadValidationProjectDocuments, ValidationProjectDocumentDto } from "../validationProjectSlice";
import { useEntityNavigation } from "../../../../app/components/EntityBreadcrumbs";
import { Package } from "../../../permissions/usePackage";

type DialogState =
    { tag: "none" }
    | { tag: "linkDocument", props: LinkValidationProjectDocumentDialogProps }
    | { tag: "createDynamicUrs" };

interface ProjectDocumentsTabProps {
    projectId: AppId;
}

export const ProjectDocumentsTab = ({ projectId }: ProjectDocumentsTabProps) => {
    const dispatch = useAppDispatch();
    const navigateToEntity = useEntityNavigation({ type: "project", id: projectId });
    const hasDynamicUrsFeature = useAppSelector(selectHasFeatureFlag(dynamicUrsFeatureFlag));
    const [documents, setDocuments] = useState<ValidationProjectDocumentDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<APIError | null>(null);
    const [dialog, setDialog] = useState<DialogState>({ tag: "none" });

    useEffect(() => {
        setLoading(true);
        dispatch(loadValidationProjectDocuments(projectId))
            .then(unwrapResult)
            .then((result) => {
                setDocuments(result);
                setError(null);
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [dispatch, projectId]);

    const handleDynamicUrsCreated = (urs: Urs) => {
        dispatch(linkValidationProjectDocument({
            id: projectId,
            payload: {
                documentId: Number(urs.id),
                order: null,
            },
        }))
            .then(unwrapResult)
            .then((document) => {
                setDocuments((current) => {
                    const index = current.findIndex((item) => item.id === document.id);
                    if (index >= 0) {
                        const next = [...current];
                        next[index] = document;
                        return next;
                    }
                    return [...current, document];
                });
                setError(null);
                setDialog({ tag: "none" });
            })
            .catch((err) => setError(err));
    };

    const columns = useMemo<GridColDef[]>(() => [
        {
            field: "order",
            headerName: "Lp",
            width: 80,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <LpField value={params.value ?? ""} />
                </Box>
            ),
        },
        { field: "no", headerName: "No", width: 180 },
        { field: "title", headerName: "Title", flex: 1, minWidth: 260 },
        { field: "type", headerName: "Type", width: 180 },
        {
            field: "status",
            headerName: "Status",
            width: 180,
            renderCell: (params) => <DocumentStatusChip status={params.value} size="small" />,
        },
        {
            field: "actions",
            headerName: "Akcje",
            width: 100,
            sortable: false,
            filterable: false,
            align: "center",
            headerAlign: "center",
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Tooltip title="Przejdź do dokumentu">
                        <IconButton size="small" onClick={() => navigateToEntity({
                            type: "document",
                            id: String(params.row.documentId),
                            documentType: params.row.type,
                        })}>
                            <LaunchIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ], [navigateToEntity]);

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            {error && <Alert severity="error">{error.kind === "http" ? error.problem?.title ?? "Request failed" : "Request failed"}</Alert>}
            <DataGrid
                autoHeight
                loading={loading}
                rows={documents}
                columns={columns}
            />
            <Package name="URS" access="ReadWrite">
                <Button size="small" variant="contained" color="primary" startIcon={<AddCircleOutlineIcon />} sx={{ mt: 2 }} onClick={() => setDialog({ tag: "createDynamicUrs" })}>
                    Dodaj URS
                </Button>
            </Package>
            <Button size="small" variant="outlined" color="primary" startIcon={<AddLinkIcon />} sx={{ mt: 2, ml: 1 }} onClick={() => setDialog({
                tag: "linkDocument",
                props: {
                    projectId,
                    onSave: (document) => {
                        setDocuments((current) => {
                            const index = current.findIndex((item) => item.id === document.id);
                            if (index >= 0) {
                                const next = [...current];
                                next[index] = document;
                                return next;
                            }
                            return [...current, document];
                        });
                        setDialog({ tag: "none" });
                    },
                    onCancel: () => setDialog({ tag: "none" }),
                },
            })}>
                Podlinkuj URS
            </Button>
            {dialog.tag === "linkDocument" && <LinkValidationProjectDocumentDialog {...dialog.props} />}
            {dialog.tag === "createDynamicUrs" && hasDynamicUrsFeature && <CreateDynamicUrsDialog open onClose={() => setDialog({ tag: "none" })} onCreated={handleDynamicUrsCreated} />}
        </Paper>
    );
};